import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { closeTutorialModal } from '../../store/actions/dashboardActions'
import { setWelcomeTourShown } from '../../hooks/useWelcomeTour.ts'
import { fireAnalytics } from '../../utils/firebase'
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts'
import { WELCOME_TOUR_STEPS } from './welcomeTourSteps.ts'
import ModalOverlay from '../UI/ModalOverlay/ModalOverlay.tsx'
import styles from './WelcomeTour.module.scss'
import welcomeTutorialSvg from '../../assets/svg/welcome-tutorial.svg'
import aiTutorialSvg from '../../assets/svg/ai-tutorial.svg'
import projectEmptyStateSvg from '../../assets/svg/project-empty-state.svg'
import profileTutorialSvg from '../../assets/svg/profile-tutorial.svg'
import materialsEmptyStateSvg from '../../assets/svg/materials-empty-state.svg'
import workOfferEmptyStateSvg from '../../assets/svg/workOffer-empty-state.svg'

const SLIDES = WELCOME_TOUR_STEPS.filter((s) => s.title).map((step) => ({
  title: step.title!,
  description: step.description,
  illustration: step.illustration,
}))

const illustrationMap: Record<string, string> = {
  'welcome-tutorial': welcomeTutorialSvg,
  'ai-tutorial': aiTutorialSvg,
  'project-empty-state': projectEmptyStateSvg,
  'profile-tutorial': profileTutorialSvg,
  'materials-empty-state': materialsEmptyStateSvg,
  'workOffer-empty-state': workOfferEmptyStateSvg,
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction === 0 ? 0 : direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 80 : -80,
    opacity: 0,
  }),
}

const WelcomeTourMobile: React.FC = () => {
  const dispatch = useDispatch()
  const [slideIndex, setSlideIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const isLastSlide = slideIndex === SLIDES.length - 1

  React.useEffect(() => {
    fireAnalytics('welcome_tour_started', {
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'welcome_tour',
      action: 'started',
    })
  }, [])

  const handleSkip = () => {
    setWelcomeTourShown('skipped')
    fireAnalytics('welcome_tour_skipped', {
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'welcome_tour',
      action: 'skipped',
      step_index: slideIndex,
    })
    dispatch(closeTutorialModal())
  }

  const handleNext = () => {
    if (isLastSlide) {
      setWelcomeTourShown('completed')
      fireAnalytics('welcome_tour_completed', {
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: 'welcome_tour',
        action: 'completed',
      })
      dispatch(closeTutorialModal())
    } else {
      setDirection(1)
      setSlideIndex((i) => i + 1)
    }
  }

  const handleBack = () => {
    if (slideIndex > 0) {
      setDirection(-1)
      setSlideIndex((i) => i - 1)
    }
  }

  const slide = SLIDES[slideIndex]
  if (!slide) return null

  return (
    <ModalOverlay
      isOpen
      onClose={handleSkip}
      zIndex={10000}
    >
      <div className={styles.modalWrapper}>
        <motion.div
          className={styles.modal}
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.slidesContainer}>
            <AnimatePresence initial={false} mode="sync" custom={direction}>
            <motion.div
              key={slideIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={styles.slide}
            >
              <div className={styles.illustrationBg}>
                {slide.illustration && illustrationMap[slide.illustration] && (
                  <motion.img
                    src={illustrationMap[slide.illustration]}
                    alt=""
                    aria-hidden
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                  />
                )}
              </div>
              <div className={styles.slideContent}>
                <motion.h2
                  className={styles.title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  {slide.title}
                </motion.h2>
                <motion.p
                  className={styles.description}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                >
                  {slide.description}
                </motion.p>
              </div>
            </motion.div>
            </AnimatePresence>
          </div>

          {SLIDES.length > 1 && (
            <div className={styles.dots}>
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`${styles.dot} ${i === slideIndex ? styles.dotActive : ''}`}
                onClick={() => {
                  if (i === slideIndex) return
                  setDirection(i > slideIndex ? 1 : -1)
                  setSlideIndex(i)
                }}
                aria-label={`Ir al paso ${i + 1}`}
              />
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleSkip}
            >
              Saltar tutorial
            </button>
            <div className={styles.navGroup}>
              {slideIndex > 0 && (
                <motion.button
                  type="button"
                  className={styles.backButton}
                  onClick={handleBack}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                <ChevronLeft size={20} />
                </motion.button>
              )}
              <motion.button
                type="button"
                className={styles.primaryButton}
                onClick={handleNext}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLastSlide ? 'Finalizar' : (
                  <>
                    Siguiente
                    <ChevronRight size={20} />
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </ModalOverlay>
  )
}

export default WelcomeTourMobile
